import React, { useCallback, useEffect, useReducer, useState } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { CustomButton } from "../CustomButton";
import { useSelectedTheme, useTranslate } from "../../../Hooks";
import { CustomCard } from "../CustomCard";
import { useVerticalNav } from "../../../Contexts/VerticalNavContext";
import { CustomInput } from "../CustomInput";
import { PhoneNumberInput } from "../PhoneNumberInput";
import {
  CityTypeIdEnum,
  ContactClassIdEnum,
  LanguageEnum,
  NationalityEnum,
} from "../../../Enums";
import { CreateContactCorporate, lookupItemsGetId } from "../../../Services";
import { CustomAutocomplete } from "../CustomAutocomplete";
import { CustomCopyToClipboard } from "../CustomCopyToClipboard";
import { DropzoneComponent } from "../DropzoneComponent";
import { GetFileTypeIcon } from "../../../Views/Home/NewContactsView/ContactOverview/UI";
import { DeleteIcon } from "../../../assets/icons";
import { formatBytes } from "react-dropzone-uploader";

// styles
import useStyles from "./styles";

const initialState = {
  classifications: [],
  country: null,
  city: null,
  landline: [],
  language: null,
  logoGUID: null,
  companyName: "",
  website: "",
};

const initialSelected = {
  classifications: null,
  country: null,
  city: null,
  language: null,
  logoGUID: null,
  website: "",
};

const initialStateData = {
  classificationsData: [],
  countryData: [],
  cityData: [],
  languageData: [],
};

const reducer = (state, { field, value }) => {
  if (field === "reset") {
    return initialState;
  }
  return { ...state, [field]: value };
};

const QuickAddCorporateView = ({}) => {
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { translate } = useTranslate("NewContactsView");
  const { addNewContact, setAlertBoxContent } = useVerticalNav();
  const [state, setState] = useReducer(reducer, initialState);
  const [selected, setSelected] = useReducer(reducer, initialSelected);
  const [data, setData] = useReducer(reducer, initialStateData);
  const [isSubmit, setIsSubmit] = useState(false);
  const [filesAccepted, setFilesAccepted] = useState([]);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleFilesAccepted = (files) => {
    setFilesAccepted(files);
  };

  const getAllLanguages = useCallback(async () => {
    try {
      const res = await lookupItemsGetId({
        lookupTypeId: LanguageEnum.lookupTypeId,
      });

      if (!(res && res.status && res.status !== 200))
        setData({ field: "languageData", value: res });
      else setData({ field: "languageData", value: [] });
    } catch (error) {}
  }, []);

  const getAllContactClass = useCallback(async () => {
    const res = await lookupItemsGetId({
      lookupTypeId: ContactClassIdEnum.lookupTypeId,
    });

    if (!(res && res.status && res.status !== 200))
      setData({ field: "classificationsData", value: res });
    else setData({ field: "classificationsData", value: [] });
  }, []);

  const GetCountries = useCallback(async () => {
    const res = await lookupItemsGetId({
      lookupTypeId: NationalityEnum.lookupTypeId,
    });

    if (!(res && res.status && res.status !== 200)) {
      setData({ field: "countryData", value: res });
    } else setData({ field: "countryData", value: [] });
  }, []);

  const GetCities = useCallback(async (countryId) => {
    const res = await lookupItemsGetId({
      lookupTypeId: CityTypeIdEnum.lookupTypeId,
      lookupParentId: countryId,
    });

    if (!(res && res.status && res.status !== 200)) {
      setData({ field: "cityData", value: res });
    } else setData({ field: "cityData", value: [] });
  }, []);

  const handleDeleteUploadedFile = (index) => {
    setUploadedFiles((prevUploadedFiles) =>
      prevUploadedFiles.filter((_, i) => i !== index)
    );
    setState({ field: "logoGUID", value: null });
  };

  const addCorporateContact = useCallback(async () => {
    try {
      setIsSubmit(true);
      const body = {
        classifications: state?.classifications?.[0]
          ? state?.classifications
          : [],
        ...(state?.country && { countryId: state?.country }),
        ...(state?.city && { cityId: state.city }),
        ...(state?.language && { languageId: state?.language }),
        landlineNumber: state?.landline,
        logoGUID: state?.logoGUID,
        companyName: state?.companyName,
        website: state?.website,
      };
      const res = await CreateContactCorporate(body);
      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: "Contact added successfully!",
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      } else {
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: "Failed to add the contact.",
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      }
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: "Some Thing wrong.",
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } finally {
      setIsSubmit(false);
    }
  }, [state]);

  useEffect(() => {
    getAllLanguages();
    getAllContactClass();
    GetCountries();
  }, []);

  useEffect(() => {
    setState({
      field: "companyName",
      value: addNewContact?.contact?.companyName,
    });
    setState({ field: "landline", value: [addNewContact?.contact?.landline] });
  }, [addNewContact]);

  useEffect(() => {
    if (uploadedFileId) {
      setState({ field: "logoGUID", value: uploadedFileId });
      const file = filesAccepted[0];
      const newFile = {
        ...file,
        fileId: uploadedFileId,
        fileSize: file.size,
        fileName: file.path,
        fileType: file.type,
      };
      setUploadedFiles([newFile]);
    } else {
      setState({ field: "logoGUID", value: null });
    }
  }, [uploadedFileId]);

  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Box className={styles.headerQuickAddCorporateView}>
        <Box className={styles.containerPercentageTitle}>
          <Typography className={styles.titleHeaderItem}>
            {translate("Quick-Add")}
          </Typography>
        </Box>
        <CustomButton
          type="submit"
          variant="contained"
          color="primary"
          onClick={() => {
            addCorporateContact();
          }}
          disabled={!state?.language || isSubmit}
        >
          {" "}
          {translate("Save")}
        </CustomButton>
      </Box>
      <CustomCard
        borderRadius="xl"
        boxShadow="xs"
        borderColor="secondary"
        classes={styles.wrapperQuickAddCorporatelView}
      >
        <Typography className={styles.titleHeaderItem}>
          {translate("Company-info")}
        </Typography>

        <Divider />

        <Box className={styles.containerItemFiled}>
          <Typography className={styles.titleFiledItem}>
            {translate("Company-name")}*
          </Typography>
          <Box className={styles.wrapperInputFiled}>
            <CustomInput
              style={{ maxWidth: "100%" }}
              hasSearchIcon={false}
              value={state?.companyName}
              disabled
            />
          </Box>
        </Box>

        <Divider />

        <Box className={styles.containerItemFiled}>
          <Typography className={styles.titleFiledItem}>
            {translate("Landline")}*
          </Typography>
          <Box className={styles.wrapperInputFiled}>
            <PhoneNumberInput
              name="landlineRef"
              idRef="landlineRef"
              isDisabled={true}
              inputPlaceholder="+1 (555) 000-0000"
              value={state?.landline?.[0]}
              country="ae"
              onInputChanged={(value) => {}}
            />
          </Box>
        </Box>

        <Divider />

        <Box className={styles.containerItemFiled}>
          <Typography className={styles.titleFiledItem}>
            {translate("Languages")}*
          </Typography>
          <Box className={styles.wrapperInputFiled}>
            <CustomAutocomplete
              placeholder={translate("Select")}
              options={data?.languageData}
              value={selected?.language || null}
              onChange={(e, value) => {
                setSelected({ field: "language", value: value });
                setState({ field: "language", value: value?.lookupItemId });
              }}
              getOptionLabel={(option) => option?.lookupItemName || ""}
              getOptionValue={(option) => option?.lookupItemName || ""}
              renderOption={(option) => option?.lookupItemName || ""}
            />
          </Box>
        </Box>

        <Divider />

        <Box className={styles.containerItemFiled}>
          <Typography className={styles.titleFiledItem}>
            {translate("Contact-class")}
          </Typography>
          <Box className={styles.wrapperInputFiled}>
            <CustomAutocomplete
              placeholder={translate("Select")}
              options={data?.classificationsData}
              value={selected?.classifications}
              onChange={(e, value) => {
                setSelected({ field: "classifications", value: value });
                setState({
                  field: "classifications",
                  value: [value?.lookupItemId],
                });
              }}
              getOptionLabel={(option) => option?.lookupItemName || ""}
              getOptionValue={(option) => option?.lookupItemName || ""}
              renderOption={(option) => option?.lookupItemName || ""}
            />
          </Box>
        </Box>
        <Divider />

        <Box className={styles.containerItemFiled}>
          <Typography className={styles.titleFiledItem}>
            {translate("Country")}
          </Typography>
          <Box className={styles.wrapperInputFiled}>
            <CustomAutocomplete
              placeholder={translate("Select")}
              options={data?.countryData}
              value={selected?.country || null}
              onChange={(e, value) => {
                setSelected({ field: "country", value: value });
                setState({ field: "country", value: value?.lookupItemId });
                setSelected({ field: "city", value: null });
                setState({ field: "city", value: null });

                if (value) GetCities(value?.lookupItemId);
                else {
                  setData({ field: "city", value: [] });
                  setSelected({ field: "city", value: null });
                  setState({ field: "city", value: null });
                }
              }}
              getOptionLabel={(option) => option?.lookupItemName || ""}
              getOptionValue={(option) => option?.lookupItemName || ""}
              renderOption={(option) => option?.lookupItemName || ""}
            />
          </Box>
        </Box>
        <Divider />

        <Box className={styles.containerItemFiled}>
          <Typography className={styles.titleFiledItem}>
            {translate("City")}
          </Typography>
          <Box className={styles.wrapperInputFiled}>
            <CustomAutocomplete
              placeholder={translate("Select")}
              options={data?.cityData}
              value={selected?.city || null}
              onChange={(e, value) => {
                setSelected({ field: "city", value: value });
                setState({ field: "city", value: value?.lookupItemId });
              }}
              getOptionLabel={(option) => option?.lookupItemName || ""}
              getOptionValue={(option) => option?.lookupItemName || ""}
              renderOption={(option) => option?.lookupItemName || ""}
            />
          </Box>
        </Box>
        <Divider />

        <Box className={styles.containerItemFiled}>
          <Typography className={styles.titleFiledItem}>
            {translate("Website-URL")}
          </Typography>
          <Box className={styles.wrapperInputFiled}>
            <Box className={styles.containerWebsiteFiled}>
              <CustomInput
                hasSearchIcon={false}
                style={{ maxWidth: "100%" }}
                placeholder={translate("Enter")}
                onChange={(e) => {
                  setSelected({ field: "website", value: e.target.value });
                  setState({ field: "website", value: e.target.value });
                }}
                value={selected.website}
              />
              <CustomCopyToClipboard
                data={state?.website ? state?.website : " "} // copy
                copyDoneShowTime={1000}
                customText={translate("Url-copied")}
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box className={styles.containerItemFiled}>
          <Typography className={styles.titleFiledItem}>
            {translate("Logo")}
          </Typography>
          <Box className={styles.wrapperInputFiled}>
            <Box className={styles.uploadedSection}>
              <DropzoneComponent
                accept={"image/*"}
                title={translate("SVG-PNG-JPG-or-GIF-max-800x400px")}
                onFilesAccepted={handleFilesAccepted}
                setUploadedFileId={setUploadedFileId}
              />
              {uploadedFiles && uploadedFiles?.length > 0 && (
                <Box className={styles.containerUpladedFilesCrads}>
                  {uploadedFiles
                    .filter((file) => file?.fileType)
                    .map((uploadFile, index) => (
                      <CustomCard
                        boxShadow="xs"
                        borderRadius="xl"
                        borderColor="secondary"
                        classes={styles.CardUploadedFilesInfo}
                        key={index}
                      >
                        {GetFileTypeIcon(uploadFile?.fileType)}
                        <Box className={styles.ContainerFileUploadedInfo}>
                          <Box className={styles.containerTitleAndDeleteIcon}>
                            <Typography className={styles.TitleFileName}>
                              {uploadFile?.fileName}
                            </Typography>
                            <DeleteIcon
                              width="20"
                              height="20"
                              className={styles.DeleteIcon}
                              style={{ cursor: "pointer" }}
                              fill={palette.button.secondary_fg}
                              onClick={() => handleDeleteUploadedFile(index)}
                            />
                          </Box>

                          <Typography className={styles.FileSizeTitle}>
                            {formatBytes(uploadFile?.fileSize)}
                          </Typography>
                        </Box>
                      </CustomCard>
                    ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CustomCard>
    </Box>
  );
};
export default QuickAddCorporateView;
